import { fetchBookpart } from '../../api/bookpart';

import { fetchNormalizedBookpartsPage } from '../../api/bookparts';

import { getBookIdFromBookpart } from '../../utils';

import cloneDeep from 'lodash/cloneDeep';

// TODO this heavy depends on the book being in the store
// so it makes it impossible to load book and bookparts async
// maybe put bookId here and sync it somewhere?

const fetchPageAction = async (commit, bookId, pageNum, callback) => {
    try {
        commit('SET_LOADING', true);

        const page = await fetchNormalizedBookpartsPage(bookId, pageNum);

        await callback(page);

        commit('SET_LOADING', false);
    } catch (e) {
        console.log(e);
        commit('SET_LOADING', false);
        throw e;
    }
};

const state = {
    pages: [],
    count: 0,
    loading: false,
    loadingInitial: false,
    activePage: 1
};

const getters = {
    loadedPages(state) {
        return state.pages.map(page => page.id);
    },
    totalPages(state) {
        return Math.ceil(state.count / 100);
    },
    canLoadNext: (state, getters) => pageNum => {
        const nextPage = pageNum + 1;

        return (
            !state.loading &&
            nextPage <= getters.totalPages &&
            !getters.loadedPages.includes(nextPage)
        );
    },
    canLoadPrev: (state, getters) => pageNum => {
        const prevPage = pageNum - 1;
        return !state.loading && prevPage > 0 && !getters.loadedPages.includes(prevPage);
    },
    pagesHaveBookpart: state => partId => {
        return !!state.pages.find(p => p.bookpartsList.includes(partId));
    },
    isPartInActivePage: state => partId => {
        const page = state.pages.find(p => p.id === state.activePage);

        if (!page) return false;

        return partId in page.bookparts;
    },

    flatSentencesList(state) {
        return state.pages.reduce((acc, page) => {
            acc = acc.concat(
                page.bookpartsList.filter(bp => {
                    const bpObj = page.bookparts[bp];

                    return bpObj.type_part.slug === 'text';
                })
            );
            return acc;
        }, []);
    },

    getBookpartById: state => id => {
        const page = state.pages.find(p => id in p.bookparts);
        if (!page) return null;

        return page.bookparts[id];
    }
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },
    SET_LOADING_INITIAL(state, status) {
        state.loadingInitial = status;
    },
    SET_PAGE(state, page) {
        state.pages = [page];
    },
    SET_PAGES(state, pages) {
        state.pages = pages;
    },
    SHIFT_PAGE(state) {
        state.pages.splice(0, 1);
    },
    UNSHIFT_PAGE(state, page) {
        if (state.pages.some(p => p.id === page.id)) return;
        state.pages.unshift(page);
    },
    PUSH_PAGE(state, page) {
        if (state.pages.some(p => p.id === page.id)) return;
        state.pages.push(page);
    },
    POP_PAGE(state) {
        state.pages.pop();
    },
    SET_COUNT(state, count) {
        state.count = count;
    },
    CLEAR_PAGES(state) {
        state.pages = [];
        state.count = 0;
        state.activePage = 1;
    },
    SET_ACTIVE_PAGE(state, page) {
        state.activePage = page;
    },
    UPDATE_BOOKPART(state, bookpart) {
        if (!state.pages.length) return;

        const page = state.pages.find(p => p.bookparts.hasOwnProperty(bookpart.id));

        if (!page) return;

        let bp = page.bookparts[bookpart.id];

        if (!bp) return;

        page.bookparts[bookpart.id] = bookpart;
    },

    // temp
    PLAYER_UPDATE_BOOKPART() {}
};

const actions = {
    /**
     * fetches initial bookparts pages for editor
     * @param payload - payload of the action
     * @param payload.page - target page
     *
     * this parameters are for scroll and selection management in editor
     * see Editor.vue line 261 subscribtion for this action
     * @param {number} payload.id - id of the bookpart to be scrolled to after fetch
     * @param {boolean} payload.select - should the bookpart be selected in bookpart editor
     * @param {boolean} payload.preserveScroll - should editor text scroll be set to previous value after fetch
     * @param {boolean} payload.noScroll - do not do anything scroll related after fetch
     */
    async fetchInitialBookparts(
        { commit, rootGetters, dispatch, getters },
        { page: pageNum, preserveScroll = false }
    ) {
        pageNum = Number(pageNum);

        if (preserveScroll && getters.loadedPages.includes(pageNum)) {
            await dispatch('refreshLoadedPages');
            return;
        }

        try {
            commit('SET_LOADING_INITIAL', true);
            await fetchPageAction(commit, rootGetters.bookId, pageNum, async page => {
                commit('SET_PAGE', page);
                commit('SET_COUNT', page.bookpartsCount);
            });
            await dispatch('setActivePage', pageNum);
        } catch (e) {
            console.error(e);
        } finally {
            commit('SET_LOADING_INITIAL', false);
        }
    },

    async refreshLoadedPages({ state, commit, rootGetters }) {
        const existingById = new Map(state.pages.map(p => [p.id, p]));
        const ids = state.pages.map(p => p.id);
        if (!ids.length) return;

        commit('SET_LOADING', true);
        try {
            const fresh = await Promise.all(
                ids.map(id =>
                    fetchNormalizedBookpartsPage(rootGetters.bookId, id).catch(e => {
                        // страница реально исчезла (сдвиг count) — только тогда отбрасываем.
                        // на любую другую ошибку (сеть, 5xx) сохраняем текущую версию страницы,
                        // чтобы транзиентный сбой не стирал уже отрендеренное окно
                        if (e && e.status === 404) return null;
                        return existingById.get(id) || null;
                    })
                )
            );
            const pages = fresh.filter(Boolean);
            commit('SET_PAGES', pages);
            if (pages.length) {
                commit('SET_COUNT', pages[0].bookpartsCount);

                // если активная страница пропала (кол-во страниц сократилось),
                // переключаемся на последнюю оставшуюся, чтобы не показывать
                // невалидное "Page X / Y"
                if (!pages.some(p => p.id === state.activePage)) {
                    commit('SET_ACTIVE_PAGE', pages[pages.length - 1].id);
                }
            }
        } finally {
            commit('SET_LOADING', false);
        }
    },

    fetchNextPage({ commit, rootGetters }, pageNum) {
        pageNum = Number(pageNum);

        return fetchPageAction(commit, rootGetters.bookId, pageNum + 1, async page => {
            commit('PUSH_PAGE', page);
        });
    },

    fetchPrevPage({ commit, rootGetters }, pageNum) {
        pageNum = Number(pageNum);
        return fetchPageAction(commit, rootGetters.bookId, pageNum - 1, async page => {
            commit('UNSHIFT_PAGE', page);
        });
    },

    async setActivePage({ commit, dispatch, state, getters }, pageNum) {
        pageNum = Number(pageNum);

        commit('SET_ACTIVE_PAGE', pageNum);

        if (pageNum < getters.totalPages && getters.canLoadNext(pageNum)) {
            await dispatch('fetchNextPage', pageNum);

            if (state.pages.length > 3) {
                commit('SHIFT_PAGE');
            }
        }

        if (pageNum === state.pages[0].id && getters.canLoadPrev(pageNum)) {
            await dispatch('fetchPrevPage', pageNum);

            if (state.pages.length > 3) {
                commit('POP_PAGE');
            }
        }

        commit('SET_LOADING_INITIAL', false);
    },

    async updateWithRefetch({ commit, dispatch, rootGetters, getters }, params) {
        // Bookparts no longer carry a `book` field, so getBookIdFromBookpart may
        // return undefined — only enforce the book match when the id is present.
        const partBookId = getBookIdFromBookpart(params);

        if (
            (partBookId !== undefined && partBookId !== rootGetters.bookId) ||
            !getters.pagesHaveBookpart(params.id)
        ) {
            return;
        }

        const bookpart = await fetchBookpart(params.id);
        commit('PLAYER_UPDATE_BOOKPART', cloneDeep(bookpart));
        commit('UPDATE_BOOKPART', bookpart);
        dispatch('setBookModifiedBuild', {}, { root: true });
    },

    clearBookparts({ commit }) {
        commit('CLEAR_PAGES');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
